'use client';
import React, { useState, useEffect } from 'react';

// Components
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogTrigger,
} from '@src/v3/components/coreUI/dialog';
import Loader from '@src/v3/components/loader';

// Step Components
import {
  PhoneStep,
  SmsStep,
  EmailStep,
  AppleErrorStep,
  Step,
  LoginModalProps,
  ApiError,
} from './components';

// Services
import {
  sendLoginPhoneOtp,
  sendLoginEmailOtp,
  verifyLoginPhoneOtp,
  verifyLoginEmailOtp,
  loginWithGoogle,
  loginWithApple,
  verifyOAuthOtp,
  editRenter,
  editAgent,
  editLandlord,
  sendSignupEmailOtp,
  getUserProfile,
  checkEmailExist,
  sendBasicPhoneOtp,
} from '@src/v3/services/auth';
import { checkRenterInvite } from '@src/v3/services/application';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';

// Utils
import { getItem, setItem } from '@src/v3/utils/localStorage';
import {
  ZEME_SIGNUP_IN_PROGRESS,
  ZEME_TEMP_SESSION_TOKEN,
  ZEME_USER_DATA,
  ZEME_VERIFIED_PHONE,
  ZEME_APPROVAL_DATA,
} from '@src/v3/constants/localStorage';
import { APP_ROUTE } from '@src/v3/constants/routes';
import { useRouter, usePathname } from 'next/navigation';
import { showErrorMsg } from '@src/v3/utils/notifications';
import {
  EmailVerificationStep,
  RegisterFormValues,
  UpdateEmailStep,
  UpdatePhoneStep,
  UserDetailsStep,
  UserType,
  WorkDetailsStep,
} from '../registerModal/components';
import {
  formatPhoneForApi,
  transformRenterProfile,
} from '@src/v3/utils/helper';
import { getAccessToken } from '@src/utils/localStorage';
import { getUserId } from '@src/v3/utils/localStorage';
import { ZEME_USERS_ROLE, LOGIN_STEPS } from '@src/v3/constants/configs';
import useProfile from '@src/v3/hooks/useProfile';
import {
  ERROR_MESSAGES,
  VALIDATION_MESSAGES,
} from '@src/v3/constants/messages';
import { identifyPostHogUser } from '@src/v3/utils/analytics';

interface UserDetails extends RegisterFormValues {
  user_id: string;
}

const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  trigger,
  overlayClassName,
  onOpenRegisterModal,
  inviteToken,
  redirectUrl,
  onSuccess,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const { setIsUserLoggedIn, fetchMyProfile } = useProfile();
  const [step, setStep] = useState<Step>(LOGIN_STEPS.PHONE);
  const [userDetails, setUserDetails] = useState<UserDetails>({
    user_id: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    code: '',
    userType: 'renter',
  });
  const [userDetailsError, setUserDetailsError] = useState<string>('');
  const [agentUserDetails, setAgentUserDetails] =
    useState<RegisterFormValues | null>(null);
  const [workDetailsError, setWorkDetailsError] = useState<string>('');
  const [isWorkDetailsLoading, setIsWorkDetailsLoading] =
    useState<boolean>(false);
  const [isVerifiedEmail, setIsVerifiedEmail] = useState<boolean>(true);
  const [isVerifiedPhone, setIsVerifiedPhone] = useState<boolean>(true);
  const [isCompnyPhotoURL, setIsCompnyPhotoURL] = useState<boolean>(false);
  // OAuth states
  const [isOAuthFlow, setIsOAuthFlow] = useState<boolean>(false);
  const [oauthUserInfo, setOauthUserInfo] = useState<{
    name?: string;
    email?: string;
    picture?: string;
  } | null>(null);
  // Apple error states
  const [appleErrorType, setAppleErrorType] = useState<
    'private_relay' | 'email_not_found' | 'general'
  >('private_relay');

  // Loading states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSmsLoading, setIsSmsLoading] = useState<boolean>(false);

  // Countdown timer states (in seconds)
  const [phoneResendTimer, setPhoneResendTimer] = useState<number>(0);
  const [emailResendTimer, setEmailResendTimer] = useState<number>(0);
  const [isUserDetailsLoading, setIsUserDetailsLoading] =
    useState<boolean>(false);
  const [isEmailVerificationLoading, setIsEmailVerificationLoading] =
    useState<boolean>(false);
  const [emailResendError, setEmailResendError] = useState<string>('');
  const [isUpdateEmailLoading, setIsUpdateEmailLoading] =
    useState<boolean>(false);
  const [isUpdatePhoneLoading, setIsUpdatePhoneLoading] =
    useState<boolean>(false);
  const [savedCropState, setSavedCropState] = useState<{
    scale: number;
    crop: { x: number; y: number };
  } | null>(null);
  // Reset function to reset all modal state to initial values
  const resetModalState = () => {
    setStep(LOGIN_STEPS.PHONE);
    setUserDetails({
      user_id: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      code: '',
      userType: 'renter',
      consent: false,
    });
    setIsLoading(false);
    setIsSmsLoading(false);
    setPhoneResendTimer(0);
    setEmailResendTimer(0);
    setIsOAuthFlow(false);
    setOauthUserInfo(null);
    setAppleErrorType('private_relay');
    setEmailResendError('');
    setIsUpdateEmailLoading(false);
    setSavedCropState(null);
    setIsCompnyPhotoURL(false);
  };

  // Handle modal close with reset
  const handleModalClose = () => {
    const signupInProgress =
      localStorage.getItem(ZEME_SIGNUP_IN_PROGRESS) === 'true';
    if (signupInProgress) {
      localStorage.removeItem(ZEME_USER_DATA);
      localStorage.removeItem(ZEME_SIGNUP_IN_PROGRESS);
      setIsUserLoggedIn?.(false);
    }
    localStorage.removeItem(ZEME_TEMP_SESSION_TOKEN);
    localStorage.removeItem(ZEME_VERIFIED_PHONE);
    resetModalState();
    onClose();
  };

  // Countdown timer effects
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (phoneResendTimer > 0) {
      interval = setInterval(() => {
        setPhoneResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [phoneResendTimer]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (emailResendTimer > 0) {
      interval = setInterval(() => {
        setEmailResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [emailResendTimer]);

  const canResendPhone = phoneResendTimer === 0;
  const canResendEmail = emailResendTimer === 0;
  const EMAIL_RESEND_TIMER = 120;
  const PHONE_RESEND_TIMER = 120;

  // Handle phone submission with API call
  const handlePhoneSubmit = async (values: { phone: string }) => {
    setIsLoading(true);
    try {
      const phoneDigits = formatPhoneForApi(values.phone);
      const response = await sendLoginPhoneOtp(phoneDigits);
      if (response?.session_token) {
        setItem(ZEME_TEMP_SESSION_TOKEN, response?.session_token);
        setUserDetails({ ...userDetails, phone: values.phone });
        setStep(LOGIN_STEPS.SMS);
      }
    } catch (error) {
      console.error('Failed to send OTP:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle SMS verification with API call
  const handleSmsSubmit = async (values: { phone: string; code: string }) => {
    setIsSmsLoading(true);
    try {
      const phoneDigits = formatPhoneForApi(values.phone);
      if (isOAuthFlow) {
        const response = await verifyOAuthOtp(phoneDigits, values.code);
        if (response?.access_token) {
          localStorage.removeItem(ZEME_SIGNUP_IN_PROGRESS);
          localStorage.removeItem(ZEME_TEMP_SESSION_TOKEN);
          setItem(ZEME_USER_DATA, response);

          // Call renter patch API to update phone number (for both CASE 1 and CASE 2)
          // Note: verifyOAuthOtp should have already set verifiedSms to true
          const userId = getUserId();
          if (userId) {
            try {
              const payload = {
                general_information: {
                  phone_number: phoneDigits,
                },
              };
              await editRenter(payload, userId);
            } catch (patchError) {
              console.error('Failed to update phone number:', patchError);
              // Continue with login even if patch fails
            }
          }

          // Fetch profile with retry logic to ensure verifiedSms is set
          // IMPORTANT: Do this BEFORE setting isUserLoggedIn to prevent redirect to VERIFY_CODE
          let profileVerified = false;
          if (fetchMyProfile) {
            let retries = 0;
            const maxRetries = 5;

            const fetchProfileWithRetry = async (): Promise<void> => {
              return new Promise((resolve) => {
                fetchMyProfile(async (profile) => {
                  if (profile?.generalInformation?.verifiedSms) {
                    profileVerified = true;
                    resolve();
                    return;
                  }

                  // If profile doesn't have verifiedSms yet and we haven't exceeded retries
                  if (!profileVerified && retries < maxRetries) {
                    retries++;
                    // Wait before retrying (exponential backoff)
                    const delay = Math.min(300 * retries, 1000);
                    await new Promise((resolveDelay) =>
                      setTimeout(resolveDelay, delay)
                    );
                    await fetchProfileWithRetry();
                    resolve();
                  } else {
                    // Max retries reached, proceed anyway
                    resolve();
                  }
                });
              });
            };

            // Wait for profile to be verified before setting isUserLoggedIn
            await fetchProfileWithRetry();
          }

          // Now set isUserLoggedIn to true - profile should already have verifiedSms set
          // This prevents the redirect to VERIFY_CODE in userProfile.tsx useEffect
          if (setIsUserLoggedIn) {
            setIsUserLoggedIn(true);
          }

          // Check for renter invite
          let hasInvite = false;
          try {
            const inviteResponse = await checkRenterInvite();
            hasInvite = inviteResponse?.has_invite || false;
          } catch (inviteError) {
            console.error('Failed to check renter invite:', inviteError);
          }

          handleModalClose();

          // Small delay to ensure profile state is updated before navigation
          await new Promise((resolve) => setTimeout(resolve, 100));

          // Handle redirect logic
          if (onSuccess) {
            onSuccess();
          } else if (redirectUrl) {
            handleModalClose();
            router.push(redirectUrl);
          } else if (inviteToken) {
            window?.location?.reload();
          } else if (hasInvite) {
            router.push(APP_ROUTE.RENTER_MY_APPLICATION);
          } else if (pathname === APP_ROUTE.EXPLORE) {
            window?.location?.reload();
          } else {
            router.push(APP_ROUTE.EXPLORE);
          }
        }
      } else {
        const session = (getItem(ZEME_TEMP_SESSION_TOKEN) as string) || '';
        const response = await verifyLoginPhoneOtp(
          phoneDigits,
          values.code,
          session
        );
        setUserDetails({
          ...userDetails,
          email: response.email,
          userType: response.account_type?.toLowerCase() as UserType,
          user_id: response.user_id || '',
        });
        setIsVerifiedEmail(response?.verified_email);

        if (response?.is_registration_complete) {
          if (response?.email && response?.verified_email) {
            await sendLoginEmailOtp(response.email, session);
            setStep(LOGIN_STEPS.EMAIL);
            return;
          } else {
            setItem(ZEME_USER_DATA, response);
            setItem(ZEME_SIGNUP_IN_PROGRESS, 'true');
            setStep(LOGIN_STEPS.EMAIL);
            return;
          }
        } else if (!response?.is_registration_complete) {
          if (
            response?.access_token &&
            response?.account_type &&
            !response?.email
          ) {
            setItem(ZEME_USER_DATA, response);
            setItem(ZEME_SIGNUP_IN_PROGRESS, 'true');
            const profileResponse = await getUserProfile(
              response?.account_type?.toLowerCase(),
              String(response?.user_id || '')
            );
            const profile = transformRenterProfile(profileResponse);
            if (profile) {
              setUserDetails({
                ...userDetails,
                user_id: profile?.id,
                phone: phoneDigits,
                code: '',
                userType: response?.account_type?.toLowerCase() as UserType,
                firstName: profile?.generalInformation?.firstName,
                lastName: profile?.generalInformation?.lastName,
                middleName: profile?.generalInformation?.middleName,
                licenseNumber: profile?.agentInformation?.licenseNumber,
                email: profile?.generalInformation?.email,
              });
              setAgentUserDetails({
                ...agentUserDetails,
                phone: phoneDigits,
                code: '',
                userType: response?.account_type?.toLowerCase() as UserType,
                firstName: profile?.generalInformation?.firstName,
                lastName: profile?.generalInformation?.lastName,
                licenseNumber: profile?.agentInformation?.licenseNumber,
                email: profile?.generalInformation?.email,
                consent: false,
                companyName: profile?.agentInformation?.companyName,
                companyAddressLine1:
                  profile?.agentInformation?.companyPrimaryAddress,
                companyAddressLine2:
                  profile?.agentInformation?.companyAddressLineTwo,
                companyAddressPlaceId:
                  profile?.agentInformation?.companyAddressPlaceId,
                companyPhoto: profile?.agentInformation?.profilePicture,
              });
              setIsCompnyPhotoURL(
                profile?.agentInformation?.profilePicture?.startsWith(
                  'http://'
                ) ||
                  profile?.agentInformation?.profilePicture?.startsWith(
                    'https://'
                  )
              );
            }
            setStep(LOGIN_STEPS.USER_DETAILS);
          } else if (
            response?.access_token &&
            response?.account_type &&
            response?.email
          ) {
            setItem(ZEME_USER_DATA, response);
            setItem(ZEME_SIGNUP_IN_PROGRESS, 'true');
            setStep(LOGIN_STEPS.EMAIL);
          } else if (!response?.access_token || !response?.account_type) {
            // Registration incomplete and missing access_token/account_type
            // Store verified phone and close LoginModal, then open RegisterModal
            setItem(ZEME_VERIFIED_PHONE, values.phone);
            resetModalState();
            onClose();
            if (onOpenRegisterModal) {
              onOpenRegisterModal();
            }
            return;
          } else {
            setStep(LOGIN_STEPS.USER_DETAILS);
          }
        }
      }
    } catch (error) {
      throw error;
    } finally {
      setIsSmsLoading(false);
    }
  };

  const handlePhoneResend = async () => {
    if (!canResendPhone) return;
    setIsLoading(true);
    try {
      const phoneDigits = formatPhoneForApi(userDetails?.phone ?? '');
      if (isOAuthFlow && !isVerifiedPhone) {
        await sendBasicPhoneOtp(phoneDigits, ZEME_USERS_ROLE.RENTER);
        setPhoneResendTimer(PHONE_RESEND_TIMER);
        return;
      }
      const response = await sendLoginPhoneOtp(phoneDigits);
      if (response?.session_token) {
        setItem(ZEME_TEMP_SESSION_TOKEN, response?.session_token);
        setPhoneResendTimer(PHONE_RESEND_TIMER);
      }
    } catch (error) {
      // Error handled
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailResend = async () => {
    if (!canResendEmail) return;
    setIsLoading(true);
    setEmailResendError('');
    try {
      const session = (getItem(ZEME_TEMP_SESSION_TOKEN) as string) || '';
      await sendLoginEmailOtp(userDetails?.email || '', session);
      setEmailResendTimer(EMAIL_RESEND_TIMER);
    } catch (error) {
      const errorMessage =
        (
          error as {
            response?: { data?: { message?: string } };
            message?: string;
          }
        )?.response?.data?.message ||
        (error as { message?: string })?.message ||
        'Failed to resend code. Please try again.';
      setEmailResendError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const resetOAuthState = () => {
    setIsOAuthFlow(false);
    setOauthUserInfo(null);
    setIsLoading(false);
  };

  const handleOAuthOtpFlow = async (
    isPhoneVerified: boolean,
    phone_number: string
  ) => {
    try {
      const phoneDigits = formatPhoneForApi(phone_number);
      if (isPhoneVerified) {
        await sendLoginPhoneOtp(phoneDigits);
      } else {
        await sendBasicPhoneOtp(phoneDigits, ZEME_USERS_ROLE.RENTER);
      }
      setUserDetails({ ...userDetails, phone: phone_number });
      setStep(LOGIN_STEPS.SMS);
    } catch {
      showErrorMsg('Failed to send OTP. Please try again.');
      resetOAuthState();
    }
  };

  const handleOAuthFlow = async (
    token: string,
    userInfo: Record<string, unknown>,
    loginApi: (
      // eslint-disable-next-line no-unused-vars
      token: string,
      // eslint-disable-next-line no-unused-vars
      ...args: string[]
    ) => Promise<Record<string, unknown>>,
    loginArgs: string[] = []
  ) => {
    try {
      setOauthUserInfo(
        userInfo as { name?: string; email?: string; picture?: string }
      );
      setIsOAuthFlow(true);
      setIsLoading(true);

      if (!userInfo.email) {
        console.error('No email provided for OAuth flow');
        resetOAuthState();
        return;
      }

      const signInResponse = await loginApi(token, ...loginArgs);
      const responseData = signInResponse as Record<string, unknown>;
      const phone_number = responseData?.phone_number as string | undefined;
      const access_token = responseData?.access_token;
      const verified_phone = responseData?.verified_sms as boolean;
      // Store OAuth response which should contain user_id and access_token

      if (!verified_phone) {
        if (access_token) {
          setItem(ZEME_USER_DATA, responseData);
        }
        setItem(ZEME_SIGNUP_IN_PROGRESS, 'true');
        setIsVerifiedPhone(false);
      }
      // Extract and store user_id in userDetails state
      const userId = (responseData?.user_id as string) || '';
      if (userId) {
        setUserDetails({
          ...userDetails,
          user_id: userId,
        });
      }
      if (phone_number) {
        await handleOAuthOtpFlow(verified_phone, phone_number);
        setIsLoading(false);
        return;
      } else {
        if (responseData?.user_id) {
          const profileResponse = await getUserProfile(
            ZEME_USERS_ROLE.RENTER,
            String(responseData?.user_id || '')
          );
          const profile = transformRenterProfile(profileResponse);
          if (profile) {
            setUserDetails({
              ...userDetails,
              user_id: profile?.id,
              userType: ZEME_USERS_ROLE.RENTER as UserType,
              phone: profile?.generalInformation?.phoneNumber,
              code: '',
              firstName: profile?.generalInformation?.firstName,
              lastName: profile?.generalInformation?.lastName,
              middleName: profile?.generalInformation?.middleName,
              email: profile?.generalInformation?.email,
            });
          }
        }
        // Show user details overlay to fill up renter details
        setStep(LOGIN_STEPS.USER_DETAILS);
        setIsLoading(false);
        return;
      }
    } catch (error) {
      console.error('Failed to initiate OAuth:', error);

      // Handle needs_approval error (like old flow)
      const apiError = error as ApiError;
      if (apiError?.response?.data?.needs_approval) {
        setItem(ZEME_APPROVAL_DATA, apiError.response.data);
        resetOAuthState();
        handleModalClose();
        router.push(APP_ROUTE.VERIFICATION_REQUIRED);
        return;
      }

      const errorMessage =
        apiError?.response?.data?.message ||
        apiError?.message ||
        'Login failed. Please try again.';
      showErrorMsg(errorMessage);
      resetOAuthState();
    }
  };

  // Handle Google OAuth login
  const handleGoogleOAuth = async (
    token: string,
    userInfo: {
      name?: string;
      email?: string;
      picture?: string;
    }
  ) => {
    await handleOAuthFlow(token, userInfo, loginWithGoogle);
  };

  // Unified helper to transform Apple user info
  const transformAppleUserInfo = (userInfo: {
    name?: { firstName?: string; lastName?: string };
    email?: string;
  }) => {
    return {
      name: userInfo.name
        ? `${userInfo.name.firstName || ''} ${
            userInfo.name.lastName || ''
          }`.trim()
        : '',
      email: userInfo.email || '',
      picture: undefined,
    };
  };

  // Handle Apple OAuth login
  const handleAppleOAuth = async (
    token: string,
    userInfo: {
      name?: {
        firstName?: string;
        lastName?: string;
      };
      email?: string;
    }
  ) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = jwtDecode(token);
      const userEmail = res?.email || userInfo.email || '';

      // Check for private relay email exactly like old flow:
      // if (res?.is_private_email) { showErrorMsg(...); return; }
      if (res?.is_private_email) {
        setAppleErrorType('private_relay');
        setStep(LOGIN_STEPS.APPLE_ERROR);
        return;
      }

      // Check if email is available exactly like old flow:
      // if (!userEmail) { showErrorMsg(...); return; }
      if (!userEmail) {
        setAppleErrorType('email_not_found');
        setStep(LOGIN_STEPS.APPLE_ERROR);
        return;
      }

      // Transform user info with the verified email
      const transformedUserInfo = transformAppleUserInfo({
        ...userInfo,
        email: userEmail,
      });
      const loginArgs = [
        userInfo.name?.firstName || '',
        userInfo.name?.lastName || '',
        userEmail,
      ];

      await handleOAuthFlow(
        token,
        transformedUserInfo,
        loginWithApple,
        loginArgs
      );
    } catch (error) {
      console.error('Apple OAuth error:', error);
      setAppleErrorType('general');
      setStep(LOGIN_STEPS.APPLE_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle phone login from error screen
  const handlePhoneLoginFromError = () => {
    setStep(LOGIN_STEPS.PHONE);
  };

  // Handle user details submission
  const handleUserDetailsSubmit = async (values: RegisterFormValues) => {
    if (isUserDetailsLoading) {
      return;
    }
    if (isOAuthFlow) {
      // In OAuth flow, we need name and phone number
      // First update renter with general_information, then send OTP
      if (!values.firstName || !values.lastName) {
        showErrorMsg('First name and last name are required');
        return;
      }
      if (!values.phone) {
        showErrorMsg('Phone number is required');
        return;
      }
      setIsUserDetailsLoading(true);
      try {
        const phoneDigits = formatPhoneForApi(values.phone);

        const generalInformation: Record<string, unknown> = {
          first_name: values.firstName,
          last_name: values.lastName,
          phone_number: phoneDigits,
        };

        if (values.middleName) {
          generalInformation.middle_name = values.middleName;
        }

        const userId = userDetails?.user_id || getUserId() || '';
        if (!userId) {
          showErrorMsg(
            'Something went wrong. Kindly refresh the page and try again.'
          );
          setIsUserDetailsLoading(false);
          return;
        }
        await editRenter(
          {
            general_information: generalInformation,
          },
          userId
        );

        // Then send login phone OTP
        const response = await sendBasicPhoneOtp(
          phoneDigits,
          ZEME_USERS_ROLE.RENTER
        );

        // Store session_token if returned (sendLoginPhoneOtp stores it automatically, but check anyway)
        if (response?.session_token) {
          setItem(ZEME_TEMP_SESSION_TOKEN, response.session_token);
        }

        // Update user details with phone number and name
        setUserDetails({
          ...userDetails,
          phone: values.phone,
          firstName: values.firstName,
          lastName: values.lastName,
          middleName: values.middleName,
        });

        // Navigate to SMS step for OTP verification
        // handleSmsSubmit will handle the verification using verifyOAuthOtp
        setStep(LOGIN_STEPS.SMS);
      } catch (error) {
        console.error('Failed to update user details or send OTP:', error);
        const errorMessage =
          (
            error as {
              response?: { data?: { message?: string } };
              message?: string;
            }
          )?.response?.data?.message ||
          (error as { message?: string })?.message ||
          'Failed to update user details or send OTP. Please try again.';
        showErrorMsg(errorMessage);
      } finally {
        setIsUserDetailsLoading(false);
      }
      return;
    }

    // Non-OAuth flow

    if (!values.firstName || !values.lastName || !values.email) {
      showErrorMsg('Please fill in all required fields');
      return;
    }
    if (!userDetails?.phone) {
      showErrorMsg(
        'Phone number not found. Please restart the registration process.'
      );
      return;
    }
    setIsUserDetailsLoading(true);
    try {
      const { exists } = await checkEmailExist({
        email: values.email,
      });
      if (exists) {
        setUserDetailsError(ERROR_MESSAGES.emailAlreadyExists);
        return;
      }
      setUserDetailsError('');
      //If user is agent or landlord, set user details and agent user details and go to work details step
      if (
        values.userType === ZEME_USERS_ROLE.AGENT ||
        values.userType === ZEME_USERS_ROLE.LANDLORD
      ) {
        setUserDetails({
          ...userDetails,
          userType: values.userType,
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          middleName: values.middleName,
        });
        setStep(LOGIN_STEPS.WORK_DETAILS);
        return;
      }
      if (getAccessToken() && userDetails?.user_id) {
        setUserDetails({
          ...userDetails,
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
        });
        const payload = {
          general_information: {
            first_name: values.firstName,
            middle_name: values.middleName || '',
            last_name: values.lastName,
            email: values.email,
          },
        };
        await editRenter(payload, userDetails?.user_id);
        await sendSignupEmailOtp(values.email);
        setStep(LOGIN_STEPS.EMAIL);
      }
    } catch (error) {
      console.error('Failed to complete signup:', error);
      throw error;
    } finally {
      setIsUserDetailsLoading(false);
    }
  };

  const handleSaveWorkDetails = (values: RegisterFormValues) => {
    setAgentUserDetails(values);
  };

  const handleWorkDetailsSubmit = async (values: RegisterFormValues) => {
    if (isWorkDetailsLoading) {
      return;
    }
    setWorkDetailsError(''); // Clear previous error
    if (!values.companyName || !values.companyAddressLine1) {
      setWorkDetailsError(VALIDATION_MESSAGES.fillAllRequiredFields);
      throw new Error(VALIDATION_MESSAGES.fillAllRequiredFields);
    }
    if (values.companyAddressLine1 && !values.companyAddressPlaceId) {
      setWorkDetailsError(VALIDATION_MESSAGES.selectValidAddress);
      throw new Error(VALIDATION_MESSAGES.selectValidAddress);
    }
    if (!agentUserDetails) {
      setWorkDetailsError(ERROR_MESSAGES.userDetailsNotFound);
      throw new Error(ERROR_MESSAGES.userDetailsNotFound);
    }
    setIsWorkDetailsLoading(true);
    try {
      let payload = {
        general_information: {
          first_name: userDetails?.firstName,
          middle_name: userDetails?.middleName,
          last_name: userDetails?.lastName,
          email: userDetails?.email,
        },
        agent_information: {
          license_number: values.licenseNumber,
          company_name: values.companyName,
          company_primary_address: values.companyAddressLine1,
          company_address_line_two: values.companyAddressLine2,
          company_address_placeid: values.companyAddressPlaceId,
        },
      };
      if (typeof values.companyPhoto !== 'string') {
        const payloadWithPhoto = {
          ...payload,
          agent_information: {
            ...payload.agent_information,
            profile_picture: values.companyPhoto,
          },
        };
        payload = { ...payload, ...payloadWithPhoto };
      }
      let response;
      if (userDetails?.userType === ZEME_USERS_ROLE.AGENT) {
        response = await editAgent(payload, userDetails?.user_id);
      } else {
        response = await editLandlord(payload, userDetails?.user_id);
      }
      if (response) {
        await sendSignupEmailOtp(userDetails?.email || '');
        setStep(LOGIN_STEPS.EMAIL);
      }
    } catch (error) {
      const errorMessage =
        (
          error as {
            response?: { data?: { message?: string } };
            message?: string;
          }
        )?.response?.data?.message ||
        (error as { message?: string })?.message ||
        ERROR_MESSAGES.failedToCompleteAgentSignup;
      setWorkDetailsError(errorMessage);
      throw error; // Re-throw so component can handle it
    } finally {
      setIsWorkDetailsLoading(false);
    }
  };

  const handleUpdateEmailModalClose = () => {
    setStep(LOGIN_STEPS.EMAIL);
  };
  const handleUpdatePhoneModalClose = () => {
    setStep(LOGIN_STEPS.SMS);
  };

  const handleUpdatePhoneSubmit = async (
    values: RegisterFormValues
  ): Promise<void> => {
    if (!values.phone) {
      return;
    }
    setIsUpdatePhoneLoading(true);
    try {
      const phoneDigits = formatPhoneForApi(values.phone);
      // If we are in OAuth flow, we might need to update the phone number in the backend
      if (isOAuthFlow) {
        const userId = getUserId();
        if (userId) {
          // Update phone number
          await editRenter(
            {
              general_information: {
                phone_number: phoneDigits,
              },
            },
            userId
          );
        }

        const response = await sendBasicPhoneOtp(
          phoneDigits,
          ZEME_USERS_ROLE.RENTER
        );
        if (response?.session_token) {
          setItem(ZEME_TEMP_SESSION_TOKEN, response.session_token);
        }
      }
      // Update phone state
      setUserDetails({
        ...userDetails,
        phone: values.phone,
      });
      setStep(LOGIN_STEPS.SMS);
    } catch (error) {
      console.error(
        error,
        ERROR_MESSAGES.failedToUpdatePhoneAndSendVerification
      );
      throw error;
    } finally {
      setIsUpdatePhoneLoading(false);
    }
  };

  const handleUpdateEmailSubmit = async (
    values: RegisterFormValues
  ): Promise<void> => {
    if (!values.email) {
      return;
    }

    setIsUpdateEmailLoading(true);
    try {
      const userId = getUserId();
      if (!userId) {
        showErrorMsg(
          'Something went wrong. Kindly refresh the page and try again.'
        );
        setIsUpdateEmailLoading(false);
        return;
      }
      if (!userDetails) {
        setIsUpdateEmailLoading(false);
        return;
      }
      // Update email using edit APIs based on user type
      // Note: Edit APIs don't return a new token, we use the existing token from complete signup
      switch (userDetails?.userType) {
        case ZEME_USERS_ROLE.AGENT:
          // Call editAgent to update the email
          await editAgent(
            {
              general_information: {
                email: values.email,
              },
            },
            userId
          );
          break;

        case ZEME_USERS_ROLE.LANDLORD:
          // Call editLandlord to update the email
          await editLandlord(
            {
              general_information: {
                email: values.email,
              },
            },
            userId
          );
          break;

        default:
          // Call editRenter to update the email
          await editRenter(
            {
              general_information: {
                email: values.email,
              },
            },
            userId
          );
          break;
      }

      // Send OTP to the new email using the same API as resend code
      await sendSignupEmailOtp(values.email);

      // Update email state and user details
      setUserDetails({
        ...userDetails,
        email: values.email,
      });
      setStep(LOGIN_STEPS.EMAIL);
    } catch (error) {
      throw error;
    } finally {
      setIsUpdateEmailLoading(false);
    }
  };

  const handleEmailVerificationSubmit = async (code: string) => {
    if (!userDetails.email) {
      throw new Error('Email not found');
    }
    setIsEmailVerificationLoading(true);
    try {
      const session = (getItem(ZEME_TEMP_SESSION_TOKEN) as string) || '';
      const response = await verifyLoginEmailOtp(
        userDetails.email,
        code,
        session
      );

      // Handle needs_approval error (like old flow)
      if (response?.needs_approval) {
        // showErrorMsg(response.error);
        setItem(ZEME_APPROVAL_DATA, response);
        handleModalClose();
        router.push(APP_ROUTE.VERIFICATION_REQUIRED);
        return;
      }

      if (response?.access_token) {
        localStorage.removeItem(ZEME_SIGNUP_IN_PROGRESS);
        localStorage.removeItem(ZEME_TEMP_SESSION_TOKEN);
        localStorage.removeItem(ZEME_VERIFIED_PHONE);
        setItem(ZEME_USER_DATA, response);
        identifyPostHogUser(
          response.user_id,
          response.account_type,
          userDetails.email || undefined
        );

        if (setIsUserLoggedIn) {
          setIsUserLoggedIn(true);
        }

        if (fetchMyProfile) {
          await fetchMyProfile();
        }

        handleModalClose();

        const accountType = response?.account_type?.toLowerCase() || '';
        switch (accountType) {
          case ZEME_USERS_ROLE.AGENT:
          case ZEME_USERS_ROLE.LANDLORD:
            // Check if agent/landlord is manually approved
            // (Subscription is now auto-assigned by backend, no plan selection needed)
            if (fetchMyProfile) {
              fetchMyProfile((profile) => {
                const isApproved =
                  (accountType === ZEME_USERS_ROLE.AGENT &&
                    profile?.agentInformation?.manualApproved) ||
                  (accountType === ZEME_USERS_ROLE.LANDLORD &&
                    profile?.landlordInformation?.manualApproved);
                if (!isApproved) {
                  router.push(APP_ROUTE.VERIFICATION_REQUIRED);
                } else {
                  router.push(APP_ROUTE.AGENT_DASHBOARD);
                }
              });
            } else {
              router.push(APP_ROUTE.AGENT_DASHBOARD);
            }
            break;
          default:
            // Handle redirect logic
            if (onSuccess) {
              onSuccess();
            } else if (redirectUrl) {
              handleModalClose();
              router.push(redirectUrl);
            } else {
              let hasInvite = false;
              try {
                const inviteResponse = await checkRenterInvite();
                hasInvite = inviteResponse?.has_invite || false;
              } catch (inviteError) {
                console.error('Failed to check renter invite:', inviteError);
              }
              if (inviteToken) {
                window?.location?.reload();
              } else if (hasInvite) {
                router.push(APP_ROUTE.RENTER_MY_APPLICATION);
              } else if (pathname === APP_ROUTE.EXPLORE) {
                window?.location?.reload();
              } else {
                router.push(APP_ROUTE.EXPLORE);
              }
            }
            break;
        }
      }
    } catch (error) {
      console.error('Failed to verify email OTP:', error);
      throw error;
    } finally {
      setIsEmailVerificationLoading(false);
    }
  };

  const renderStep = () => {
    const commonProps = {
      onStepChange: setStep,
      onClose: handleModalClose,
      phone: userDetails?.phone || '',
      email: userDetails?.email || '',
      canResendPhone,
      canResendEmail,
      isOAuthFlow,
      userEmail: oauthUserInfo?.email || '',
      userInfo: oauthUserInfo,
    };

    switch (step) {
      case LOGIN_STEPS.PHONE:
        return (
          <PhoneStep
            {...commonProps}
            onPhoneSubmit={handlePhoneSubmit}
            isLoading={isLoading}
            onGoogleOAuth={handleGoogleOAuth}
            onAppleOAuth={handleAppleOAuth}
            onOpenRegisterModal={onOpenRegisterModal}
          />
        );
      case LOGIN_STEPS.SMS:
        return (
          <SmsStep
            {...commonProps}
            onSmsSubmit={handleSmsSubmit}
            isSmsLoading={isSmsLoading}
            phoneResendTimer={phoneResendTimer}
            onPhoneResend={handlePhoneResend}
            hideTryAnotherNumber={isOAuthFlow && isVerifiedPhone}
          />
        );
      case LOGIN_STEPS.EMAIL:
        return (
          <EmailStep
            {...commonProps}
            emailResendTimer={emailResendTimer}
            onEmailResend={handleEmailResend}
            isEmailVerificationLoading={isEmailVerificationLoading}
            onEmailVerificationSubmit={handleEmailVerificationSubmit}
            emailResendError={emailResendError}
            hideTryAnotherEmail={isVerifiedEmail}
          />
        );
      case LOGIN_STEPS.EMAIL_VERIFICATION:
        return (
          <EmailVerificationStep
            {...commonProps}
            onStepChange={(step) => setStep(step as Step)}
            onEmailVerificationSubmit={handleEmailVerificationSubmit}
            isEmailVerificationLoading={isEmailVerificationLoading}
            emailResendTimer={emailResendTimer}
            onEmailResend={handleEmailResend}
          />
        );
      case LOGIN_STEPS.USER_DETAILS:
        return (
          <UserDetailsStep
            onStepChange={() => {}} // Not used in login flow
            onClose={handleModalClose}
            initialValues={userDetails || undefined}
            email={oauthUserInfo?.email || userDetails?.email || ''}
            selectedUserType={userDetails?.userType || ('renter' as UserType)}
            canResendPhone={canResendPhone}
            canResendEmail={canResendEmail}
            isOAuthFlow={isOAuthFlow}
            onUserDetailsSubmit={handleUserDetailsSubmit}
            isUserDetailsLoading={isUserDetailsLoading}
            userDetailsError={userDetailsError}
          />
        );
      case LOGIN_STEPS.WORK_DETAILS:
        return (
          <WorkDetailsStep
            {...commonProps}
            savedCropState={savedCropState}
            setSavedCropState={setSavedCropState}
            onStepChange={(step) => setStep(step as Step)}
            onWorkDetailsSubmit={handleWorkDetailsSubmit}
            isWorkDetailsLoading={isWorkDetailsLoading}
            initialValues={agentUserDetails || undefined}
            userDetailsData={agentUserDetails || undefined}
            onSaveWorkDetails={handleSaveWorkDetails}
            workDetailsError={workDetailsError}
            setWorkDetailsError={setWorkDetailsError}
            isDeletePhotoAllowed={!isCompnyPhotoURL}
          />
        );

      case LOGIN_STEPS.APPLE_ERROR:
        return (
          <AppleErrorStep
            {...commonProps}
            errorType={appleErrorType}
            onGoogleOAuth={handleGoogleOAuth}
            onPhoneLogin={handlePhoneLoginFromError}
          />
        );
      case LOGIN_STEPS.UPDATE_EMAIL:
        return (
          <UpdateEmailStep
            {...commonProps}
            onStepChange={(step) => setStep(step as Step)}
            onClose={handleUpdateEmailModalClose}
            onUpdateEmailSubmit={handleUpdateEmailSubmit}
            isUpdateEmailLoading={isUpdateEmailLoading}
            userDetailsData={userDetails || undefined}
          />
        );
      case LOGIN_STEPS.UPDATE_PHONE:
        return (
          <UpdatePhoneStep
            {...commonProps}
            onStepChange={(step) => setStep(step as Step)}
            onClose={handleUpdatePhoneModalClose}
            onUpdatePhoneSubmit={handleUpdatePhoneSubmit}
            isUpdatePhoneLoading={isUpdatePhoneLoading}
            userDetailsData={userDetails || undefined}
          />
        );
      default:
        return null;
    }
  };

  // Combined loading state for all steps
  const isAnyLoading =
    isLoading ||
    isSmsLoading ||
    isUserDetailsLoading ||
    isWorkDetailsLoading ||
    isEmailVerificationLoading ||
    isUpdateEmailLoading;

  const modalContent = (
    <>
      <Loader isVisible={isAnyLoading} />
      <DialogContent
        size="md"
        showCloseButton={false}
        closeOnOutsideClick={true}
        className="h-[100dvh] md:h-[790px] md:min-h-[790px] max-h-[100dvh] md:max-h-[790px] max-w-full md:max-w-[1236px] !p-0 !rounded-none md:!rounded-2xl overflow-hidden"
        tabIndex={-1}
      >
        {renderStep()}
      </DialogContent>
    </>
  );

  if (trigger) {
    return (
      <GoogleOAuthProvider
        clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ''}
      >
        <Dialog open={isOpen} onOpenChange={handleModalClose}>
          <DialogOverlay className={overlayClassName} />
          <DialogTrigger asChild>{trigger}</DialogTrigger>
          {modalContent}
        </Dialog>
      </GoogleOAuthProvider>
    );
  }

  return (
    <GoogleOAuthProvider
      clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ''}
    >
      <Dialog open={isOpen} onOpenChange={handleModalClose}>
        <DialogOverlay className={overlayClassName} />
        {modalContent}
      </Dialog>
    </GoogleOAuthProvider>
  );
};
export default LoginModal;
